import React from 'react';
import { Avatar, Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material';

/**
 * A titled panel — the container most page sections actually want.
 *
 * Pages were each assembling Card + Box + Typography + Divider by hand, with
 * different padding every time. Padding tightens on phones, where 24px of
 * chrome around a list is a meaningful share of the screen.
 */
export const SectionCard: React.FC<{
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  /** Icon shown in a tinted avatar before the title — for a dialog with
   * several stacked sections (task/contact/ninja info, say) that need to be
   * tellable apart at a glance, not just by title text. */
  icon?: React.ReactNode;
  /** Tints the icon avatar. Defaults to the accent. */
  tone?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
  /** Remove body padding — for tables that manage their own. */
  disablePadding?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, action, icon, tone = 'primary', disablePadding, children }) => (
  <Card>
    {(title || action) && (
      <>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            {icon && (
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: `${tone}.main`,
                  color: `${tone}.contrastText`,
                }}
              >
                {icon}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0 }}>
              {title && <Typography variant="h6">{title}</Typography>}
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Stack>
          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Stack>
        <Divider />
      </>
    )}
    <CardContent
      sx={{
        p: disablePadding ? 0 : { xs: 2, md: 2.5 },
        '&:last-child': { pb: disablePadding ? 0 : { xs: 2, md: 2.5 } },
      }}
    >
      {children}
    </CardContent>
  </Card>
);
